import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listCatalog } from '@frontend/BidSheets/Admin/referenceDataService';
import PipelineProjectService, {
  type CreateFieldTicketBody,
  type CreateTicketLine,
  type FieldTicket,
  type ProjectPhase,
} from '@frontend/AppSettings/services/PipelineProjectService';
import SiteService from '@frontend/AppSettings/services/SiteService';
import { PersonDayService } from '@frontend/AppSettings/services/PipelineProjectService';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';
import { useCatalogOptions } from '@frontend/BidSheets/Admin/useCatalogOptions';
import { money, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
// The same seven codes the force-account table prices by — one vocabulary (P4b decision #5).
import { FA_REASONS } from '../faExports';
import PersonDayApprovalPanel from './PersonDayApprovalPanel';
// The A8 miss vocabulary — operational, not the FA fault codes; ONE frontend copy.
import { MISS_REASONS } from './productionMissReasons';
// The design source's Over/Under/On-Plan derivation, reused for the entry-time overload
// advisory (validation set, decision f) — never re-minted.
import { crewStatus } from '../crewStatus';

/**
 * The Field Timesheet tab (Part II P4b): daily field tickets, and the payroll Long Sheet export.
 *
 * The confidentiality shape mirrors the backend exactly:
 *  - the RECORD form sends no money — a line is a catalog pick plus hours; the server resolves and
 *    freezes the rate (CreateTicketLine has no rate field, by type);
 *  - the ticket LIST renders rates only when the server sent them (they are stripped for blind
 *    callers, so `line.rate` is simply absent);
 *  - the LONG SHEET export is gated on financial visibility. Its CONTENT is hours-only (the
 *    design source's Long Sheet carries no dollars — money lives in the FA exports), but a
 *    whole-crew hours document leaving the app is payroll data, and no server-side stripping can
 *    follow an export — so the control simply does not exist for a blind role.
 */
interface FieldTimesheetTabProps {
  matterid: string;
}

interface DraftLine {
  /** The STORED line's id, carried through an edit (R3) and '' on a fresh line. Round-tripping
   * it is what lets the server upsert instead of delete-reinserting — preserving the judgement
   * columns the author does not supply: the off-baseline note, the PM's disposition, and the
   * office's authoritative billing basis. Without it, a typo fix silently destroyed all three. */
  lineId: string;
  kind: 'labour' | 'equipment';
  catalogId: string;
  /** The stored line description, carried through an EDIT (A3): the wire label normally
   * comes from the picked catalog option, but an edit can save before the options query
   * resolves — falling back here keeps the Long Sheet's name instead of '(unnamed)'. */
  description: string;
  /** '' = inherit the ticket's header code (gap review 2.9's split-day model). */
  costCodeId: string;
  qty: string;
  hours: string;
  /** The payroll split (gap 3.1) — '' = not split; OT derives as hours − reg − trav. */
  regHours: string;
  travHours: string;
  subsistence: boolean;
  truck: boolean;
  /** Equipment unit tracking (gap 3.3) — '' = no unit / no reading. Picking a unit locks
   * qty to 1 (a unit-linked line records ONE unit; the server refuses anything else). */
  unitId: string;
  meterStart: string;
  meterEnd: string;
  /** P7a (0049): the person whose hours this row is. '' = a legacy crew line, which still
   *  writes exactly as before — the fan-out is additive. When set, the server requires qty 1. */
  employeeId: string;
  /** The ENTERED axis: 'working' | 'travel' | '' (not stated). Its verified counterpart is
   *  P7b's; nothing on this screen classifies anything. */
  hourType: string;
  /** The line's OWN stored site/basis, carried through an edit (review R5). The schema is
   *  per-line ("a person who moved between sites"), the form is per-day — so an edit keeps
   *  each line's stored value unless the author touches the day-level control, and the
   *  day-level value fills only the lines that carry none. '' = the line carries none. */
  siteId: string;
  basis: string;
  /** P7b (0050): WHY these hours are force account, carried through an edit for the reason the
   *  catalog ids are — without it a save would strip the reason off every line and the
   *  `force_account` basis beside it would become unsatisfiable. '' = none stated. */
  faReason: string;
}

const EMPTY_LINE: DraftLine = {
  lineId: '',
  kind: 'labour', catalogId: '', description: '', costCodeId: '', qty: '1', hours: '',
  regHours: '', travHours: '', subsistence: false, truck: false,
  unitId: '', meterStart: '', meterEnd: '',
  employeeId: '', hourType: '', siteId: '', basis: '', faReason: '',
};

const EMPTY_FORM = {
  ticket_date: '', project_phase_id: '', project_cost_code_id: '',
  //: W4b — WHERE the labour happened, which is what picks the law when P7b classifies (d.14).
  //: Per-DAY grain: a crew works one spread. The line column can still disagree, because the
  //: schema supports a person who moved between sites.
  site_id: '',
  //: W4c — the basis PROPOSED at entry (d.72), supervisor-overridable. Blank is a real answer:
  //: "nobody stated a basis" is a true thing for 7b's report to read, and better than a
  //: `standard` nobody chose.
  billing_basis_field: '',
  // P7b (0050): WHY the day's hours are force account, proposed once and applied per LABOUR line.
  // It is a NEW field beside the header's `reason_code` below, not the same one: the header's
  // reason attributes the whole out-of-scope DAY, this one attributes the hours the commercial
  // sweep will carry — and A5 put it on the line so two reasons on one day are representable.
  fa_reason_code: '',
  weather: '', temp_high_c: '', temp_low_c: '',
  contractor_rep: '', customer_rep: '',
  production_m: '', man_hours: '', equipment_hours: '',
  is_force_account: false, reason_code: 'weather',
  production_miss_reason: '', production_miss_note: '',
  notes: '',
  //: A7/D5: blank is a real answer (day-grain classification), never defaulted. Controlled so
  //: the field can DISPLAY the stored value on edit and so an untouched blur has nothing to
  //: post (review R1 — the uncontrolled field erased the crew's shift start on a tab-through).
  shift_start: '',
};

const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** An explicit "no site" is a CHOICE with its own token — deriving it from '' made
 *  "(none recorded)" unpickable on a one-site matter (see effectiveSiteId below). Module scope
 *  because startEdit needs it too: a siteless edited day must rehydrate to this, or the lone
 *  active site would be fabricated onto lines nobody recorded a site for (review R5). */
const NO_SITE = '__none__';

/** How many person-days the approval section renders at once.
 *
 *  Each panel reads its own readiness on mount, and approval is CONTIGUOUS — a period is approved
 *  in order, so the OLDEST unapproved day is the one that matters and the tail is unreachable
 *  until it is done. The cap therefore trims the TAIL: an oldest-first slice shows the days that
 *  unblock the rest, and a newest-first one would hide exactly those. */
const APPROVAL_PANEL_LIMIT = 25;

/** rows: one per distinct line description, columns: one per recorded date (Σ qty×hours),
 * then the weekly payroll block (gap 3.1/3.2): Reg / Trav / OT — the source's own formula,
 * OT = Total − Reg − Trav, computed ONLY over lines that carry a split (an unsplit line's
 * hours count in Total but claim no overtime; fabricating a split would misstate payroll) —
 * and the Sub / Tru person-day counts (Σ qty over flagged lines). */
/** Hands on the phase that day — the F1 rule, in the one place every reader should ask.
 *
 *  A bare `Σ qty` counts a person twice from 0049 onward: the entered axis gives a travel hour
 *  its own row, so one person working 10h and travelling 3h is TWO rows. Distinct people on
 *  WORKING rows (a travel-only day is not a hand on site), plus `Σ qty` over the personless
 *  legacy rows, which are exactly what they were. Man-hours are NOT computed here and must keep
 *  counting every row — today's `hours` already contain travel, so filtering rows out of THAT
 *  number would shrink a figure 0049 does not move.
 */
export function countHands(lines: { kind: string; qty?: string | number;
  employeeid?: string | null; hour_type_entered?: string | null }[]): number {
  const people = new Set<string>();
  let legacy = 0;
  for (const line of lines) {
    if (line.kind !== 'labour') continue;
    if (line.employeeid) {
      if ((line.hour_type_entered ?? 'working') === 'working') people.add(line.employeeid);
    } else {
      // `qty` arrives as a string over the wire and as a number from the draft form; both are
      // the same fact, so it is normalised here rather than at every call site.
      legacy += num(line.qty === undefined ? '' : String(line.qty)) ?? 0;
    }
  }
  return people.size + legacy;
}

/** Per-person STATUTORY premium hours, keyed on `employeeid` (counsel D4, register §E row 35).
 *
 * `undefined` for a person means NO CLASSIFIED DAY IN RANGE, which is a different fact from zero
 * premium — and the columns render BLANK for it, never 0. The same rule the entered-axis OT
 * already keeps one column over: only the classifier may say a day had no overtime.
 *
 * OVERTIME AND DOUBLE-TIME ARE SEPARATE FIGURES (R5), because they are paid at different
 * multipliers and the calculator genuinely emits both — the plan's own TS-02 case is a BC
 * 13-hour day classifying 8/4/1. One blended number invited a clerk to pay the whole figure at
 * 1.5×, underpaying every double-time hour: counsel D4's documented liability case, on the very
 * column built to prevent it. The payload already separates the types; the sheet must not
 * collapse them. */
export function statutoryPremiumByPerson(
  hours: { employeeid: string; hour_type: string; hours: string }[],
): Map<string, { overtime: number; doubleTime: number }> {
  const out = new Map<string, { overtime: number; doubleTime: number }>();
  for (const row of hours) {
    // `regular` still creates the KEY, so a classified person with no premium reads 0 rather
    // than blank — which is exactly the distinction the blank is protecting.
    const current = out.get(row.employeeid) ?? { overtime: 0, doubleTime: 0 };
    if (row.hour_type === 'overtime') current.overtime += num(row.hours) ?? 0;
    else if (row.hour_type === 'double_time') current.doubleTime += num(row.hours) ?? 0;
    out.set(row.employeeid, current);
  }
  return out;
}

export function buildLongSheetRows(
  tickets: FieldTicket[],
  /** From `GET /projects/<id>/classified-hours`. Omitted before classification is enabled, which
   * is what leaves the statutory columns blank on every row of a pre-7b sheet. */
  statutoryPremium?: Map<string, { overtime: number; doubleTime: number }>,
): (string | number)[][] {
  const dates = Array.from(new Set(tickets.map((t) => t.ticket_date))).sort();
  type Acc = { name: string; employeeid: string | null;
    perDay: Map<string, number>; reg: number; trav: number; ot: number;
    unsplit: number; entered: number; sub: number; tru: number;
    subDays: Set<string>; truDays: Set<string> };
  const byName = new Map<string, Acc>();
  for (const ticket of tickets) {
    for (const line of ticket.lines) {
      if (line.kind !== 'labour') continue;
      // P7a (register F4): rows print the DESCRIPTION AS TYPED — the fan-out writes
      // "Role — Employee Name", so per-person rows fall out with the role vocabulary intact,
      // and one person on two roles prints as two rows. A personed line whose description does
      // not already carry the name gets it composed on (review D1 — self-entry's bare "Labour"
      // must not print nameless on a payroll grid); the person also rides the grouping key, so
      // identical labels cannot merge two people, and a personless legacy line keys on its
      // description exactly as it always did.
      const typed = line.description || '';
      const person = line.employeeid ? (line.employee_name || '') : '';
      const name = person
        ? (typed && typed.endsWith(`— ${person}`) ? typed
          : (typed ? `${typed} — ${person}` : person)) || '(unnamed)'
        : (typed || '(unnamed)');
      const key = `${line.employeeid ?? ''}|${name}`;
      const acc = byName.get(key)
        ?? { name, employeeid: line.employeeid ?? null,
             perDay: new Map<string, number>(), reg: 0, trav: 0, ot: 0, unsplit: 0,
             entered: 0, sub: 0, tru: 0,
             subDays: new Set<string>(), truDays: new Set<string>() };
      const qty = num(line.qty) ?? 0;
      const hours = num(line.hours) ?? 0;
      acc.perDay.set(ticket.ticket_date, (acc.perDay.get(ticket.ticket_date) ?? 0) + qty * hours);
      const reg = num(line.reg_hours);
      const trav = num(line.trav_hours);
      if (line.hour_type_entered) {
        // The ENTERED axis (0049), read here so the 7a window does not lose the travel split
        // (register F3): every post-0049 line would otherwise fall to the unsplit branch below
        // and pile travel hours under "Reg" on a payroll document.
        //
        // This is presentation of an entered FACT, not a classification — `Reg` here means "not
        // travel", exactly what it has always meant on this sheet. What the statute calls
        // regular is P7b's, and it lives in `classified_blocks` rather than in a column beside
        // this one — the `Statutory OT` column reads it, and this one does not. OT stays blank
        // for these rows rather than 0: a fabricated zero would read as "no overtime", which is
        // a claim only the classifier may make.
        if (line.hour_type_entered === 'travel') acc.trav += qty * hours;
        else acc.reg += qty * hours;
        acc.entered += qty * hours;
      } else if (reg !== undefined || trav !== undefined) {
        acc.reg += qty * (reg ?? 0);
        acc.trav += qty * (trav ?? 0);
        acc.ot += qty * Math.max(0, hours - (reg ?? 0) - (trav ?? 0));
      } else {
        // The cross-foot column: without it, Total ≠ Reg+Trav+OT on any mixed week and a
        // payroll clerk has no cue where the hours went (review). Unsplit hours are counted,
        // not fabricated into a split.
        acc.unsplit += qty * hours;
      }
      // Person-distinct where the line names somebody (register F1): the entered axis gives a
      // travel hour its own row, so adding `qty` on both would count one person's day twice.
      if (line.subsistence) {
        if (line.employeeid) acc.subDays.add(ticket.ticket_date);
        else acc.sub += qty;
      }
      if (line.truck) {
        if (line.employeeid) acc.truDays.add(ticket.ticket_date);
        else acc.tru += qty;
      }
      byName.set(key, acc);
    }
  }
  // P7b (counsel D4). The STATUTORY column is added BESIDE the entered one rather than replacing
  // it, and that is the dual-meaning window's own shape: the legacy figure may be DISPLAYED while
  // the statutory one is what anyone pays from, and the legacy FORMULA retires when the window
  // closes. Removing the legacy column outright is what A2 forbids — those columns are the only
  // record of the field's own claim, and they stay off P9's deletion list.
  const header = ['NAME', ...dates, 'Total', 'Reg', 'Trav', 'OT', 'Statutory OT', 'Statutory DT',
    'Unsplit', 'Sub', 'Tru'];
  const credited = new Set<string>();
  const rows = Array.from(byName.values()).map((acc) => {
    const cells = dates.map((d) => acc.perDay.get(d) ?? 0);
    // A row whose hours came from the entered axis shows NO overtime figure — blank, not 0.
    // Only the classifier (P7b) may say a day had no overtime; this sheet only knows what the
    // field entered. A mixed migration-boundary week still shows the pre-0049 rows' OT, with the
    // unsplit note covering the rest — the disclosure precedent already in this file.
    const ot: string | number = acc.entered > 0 && acc.ot === 0 ? '' : acc.ot;
    // The statutory figures, or BLANK when this person has no classified day in range. Blank and
    // zero are different facts and the sheet must not conflate them: zero says the classifier
    // looked and found no premium, blank says nobody has approved the day yet. TWO columns (R5):
    // overtime pays 1.5× and double-time 2×, and one blended figure invited a clerk to pay it
    // all at 1.5× — underpaying every double-time hour on the column built to prevent exactly
    // that.
    //
    // CREDITED ONCE PER PERSON, NOT PER ROW, and that is the whole of it. Rows key on
    // (person, label) — one person on two roles is deliberately two rows — while the aggregate is
    // per PERSON, so printing it on every row of theirs would pay one person's premium twice on a
    // payroll document. Measured: Ann Boyd, owed 6 hours, totalled 12 across a Labourer row and a
    // Foreman row. The backend chose person-day grain expressly to prevent this; the sheet must
    // not reintroduce it. Subsequent rows for the same person print BLANK rather than 0, because
    // 0 would claim the classifier found no premium on them.
    const premium = (acc.employeeid && !credited.has(acc.employeeid))
      ? statutoryPremium?.get(acc.employeeid) : undefined;
    const statutoryOt: string | number = premium ? premium.overtime : '';
    const statutoryDt: string | number = premium ? premium.doubleTime : '';
    if (acc.employeeid) credited.add(acc.employeeid);
    return [acc.name, ...cells, cells.reduce((s, v) => s + v, 0),
      acc.reg, acc.trav, ot, statutoryOt, statutoryDt, acc.unsplit,
      acc.sub + acc.subDays.size, acc.tru + acc.truDays.size];
  });
  const totals = dates.map((_, i) => rows.reduce((s, r) => s + Number(r[i + 1] || 0), 0));
  // A TOTAL OF BLANKS IS BLANK, NOT ZERO — and this is the same rule the cells already keep, which
  // the totals row was quietly breaking on the columns that carry it. `Number('' || 0)` is 0,
  // so a sheet where NO row states an overtime figure footed to "0" and claimed on a payroll
  // document that the classifier had looked and found none. It had not been asked.
  // Reg=0, Trav=1, OT=2, StatutoryOT=3, StatutoryDT=4, Unsplit=5, Sub=6, Tru=7. Only the three
  // OVERTIME columns carry the blank/zero distinction: the rest are quantities, and 0 hours of
  // travel on an empty sheet is a true statement. `cells.length` is 0 on an empty sheet, so a
  // blanket "every cell is blank" test would foot those to 0 and claim the classifier had looked.
  const BLANK_WHEN_NOTHING_STATED = new Set([2, 3, 4]);
  const block = [0, 1, 2, 3, 4, 5, 6, 7].map((k) => {
    const cells = rows.map((r) => r[dates.length + 2 + k]);
    if (BLANK_WHEN_NOTHING_STATED.has(k) && cells.every((v) => v === '')) return '';
    return cells.reduce((s: number, v) => s + Number(v || 0), 0);
  });
  return [header, ...rows,
    ['TOTALS', ...totals, totals.reduce((s, v) => s + v, 0), ...block]] as (string | number)[][];
}

/** The weekly per-unit Equip. Summary (gap 3.3) — the day sheets' unit-hours block, rolled up
 * from the ticket lines' registry links. One row per UNIT: Σ qty×hours per day, days used,
 * the LOWEST meter_start / HIGHEST meter_end of the range and their delta (blank until both
 * ends exist — a delta over one reading is not a fact; a mid-range meter swap can make the
 * span dishonest or even negative, and it renders RAW rather than being corrected into a
 * figure nobody read). Equipment lines WITHOUT a unit link roll into a disclosed "(unlinked)"
 * residual row rather than silently vanishing — a summary that quietly excluded them would
 * read as "these machines did all the work". */
export function buildEquipSummaryRows(tickets: FieldTicket[]): (string | number)[][] {
  const dates = Array.from(new Set(tickets.map((t) => t.ticket_date))).sort();
  type Acc = { label: string; name: string; perDay: Map<string, number>;
    meterFirst?: number; meterLast?: number };
  const byUnit = new Map<string, Acc>();
  const UNLINKED = '__unlinked__';
  for (const ticket of tickets) {
    for (const line of ticket.lines) {
      if (line.kind !== 'equipment') continue;
      const key = line.inventory_item_id ?? UNLINKED;
      const acc = byUnit.get(key) ?? {
        label: line.inventory_item_id ? (line.unit_tag ?? '(untagged)') : '(unlinked)',
        name: line.inventory_item_id
          ? (line.unit_name ?? line.description ?? '')
          : 'equipment lines without a unit',
        perDay: new Map<string, number>(),
      };
      const hours = (num(line.qty) ?? 0) * (num(line.hours) ?? 0);
      acc.perDay.set(ticket.ticket_date, (acc.perDay.get(ticket.ticket_date) ?? 0) + hours);
      const start = num(line.meter_start);
      const end = num(line.meter_end);
      if (start !== undefined) {
        acc.meterFirst = acc.meterFirst === undefined ? start : Math.min(acc.meterFirst, start);
      }
      if (end !== undefined) {
        acc.meterLast = acc.meterLast === undefined ? end : Math.max(acc.meterLast, end);
      }
      byUnit.set(key, acc);
    }
  }
  const header = ['UNIT', 'DESCRIPTION', ...dates, 'Total hrs', 'Days',
    'Meter start', 'Meter end', 'Meter Δ'];
  const entries = Array.from(byUnit.entries())
    // units sorted by tag; the unlinked residual always LAST — it is the disclosure line
    .sort(([ka, a], [kb, b]) => (ka === UNLINKED ? 1 : kb === UNLINKED ? -1
      : a.label.localeCompare(b.label)));
  const rows = entries.map(([, acc]) => {
    const cells = dates.map((d) => acc.perDay.get(d) ?? 0);
    const days = cells.filter((v) => v > 0).length;
    const delta = acc.meterFirst !== undefined && acc.meterLast !== undefined
      ? Math.round((acc.meterLast - acc.meterFirst) * 10) / 10 : '';
    return [acc.label, acc.name, ...cells, cells.reduce((s, v) => s + v, 0), days,
      acc.meterFirst ?? '', acc.meterLast ?? '', delta];
  });
  const totals = dates.map((_, i) => rows.reduce((s, r) => s + Number(r[i + 2] || 0), 0));
  return [header, ...rows,
    ['TOTALS', '', ...totals, totals.reduce((s, v) => s + v, 0), '', '', '', '']];
}

/** One (date, phase)'s bid-scope production judged against the phase's planned rate —
 * the ticket rows' status bar (validation set, decision e). */
export interface DayProductionStatus {
  date: string;
  phaseId: string;
  produced: number;
  planned: number;
  status: 'green' | 'yellow' | 'red';
}

/** Per-(date, phase) production status for the ticket rows (validation set, decision e):
 * the day's non-FA metres summed against `production_per_day_m`, with the demo's
 * thresholds ported — ratio ≥ 0.98 green, ≥ 0.88 yellow, else red. DEVIATION recorded in
 * the plan: the demo renders GREEN when planned ≤ 0; here NO rate means NO row (absence ≠
 * 0, the gate's own rule). FA tickets are excluded from the sum (the bid-scope compare is
 * what the rate prices), so an FA-only day gets no row either. Quantities only — no money
 * key is ever read, so the bars render for every audience. */
export function buildDayProductionStatus(
  tickets: FieldTicket[], phases: ProjectPhase[],
): DayProductionStatus[] {
  const rateById = new Map<string, number>();
  for (const phase of phases) {
    const rate = Number(phase.production_per_day_m);
    // NaN or 0 reads as NO RATE RECORDED, not a zero plan (the column defaults to 0).
    if (Number.isFinite(rate) && rate > 0) rateById.set(phase.id, rate);
  }
  const byDayPhase = new Map<string, DayProductionStatus>();
  for (const t of tickets) {
    if (t.is_force_account || !t.project_phase_id) continue;
    const planned = rateById.get(t.project_phase_id);
    if (planned === undefined) continue;   // no rate → no bar
    const key = `${t.ticket_date}|${t.project_phase_id}`;
    const row = byDayPhase.get(key) ?? {
      date: t.ticket_date, phaseId: t.project_phase_id,
      produced: 0, planned, status: 'red' as const,
    };
    row.produced += num(t.production_m) ?? 0;
    byDayPhase.set(key, row);
  }
  return Array.from(byDayPhase.values()).map((row) => {
    const ratio = row.produced / row.planned;
    return { ...row,
      status: ratio >= 0.98 ? 'green' : ratio >= 0.88 ? 'yellow' : 'red' };
  });
}

/** The demo's "inactive code" flag, renamed to what it really is (validation set, decision
 * g): a PHASE-WINDOW check — real cost codes carry NO activity state (0009's flags are
 * display and pricing roles), and the demo's test was day-outside-phase-window. A NULL
 * bound is unbounded on that side; no dates at all → no flag (absence ≠ inactive).
 * ADVISORY only, never a wall: work off schedule is a true fact, and blocking it would
 * falsify the record. */
export function outsidePhaseWindow(
  date: string, phaseId: string | null | undefined, phases: ProjectPhase[],
): boolean {
  if (!date || !phaseId) return false;
  const phase = phases.find((p) => p.id === phaseId);
  if (!phase) return false;
  // ISO dates compare lexicographically — no Date() timezone traps.
  return (!!phase.planned_start && date < phase.planned_start)
    || (!!phase.planned_end && date > phase.planned_end);
}

/** The daily per-code hours burn (gap A10) — "the burn Travis wants", as HOURS ONLY: rows are
 * cost codes via COALESCE(line code, header code) — the 2.9 grouping rule the dollar rollup
 * uses — columns are recorded dates, cells Σ qty×hours over ALL lines, labour AND equipment.
 * FA-day hours INCLUDED: time on site is fact whatever the work's attribution (the
 * production_summary/crew-loading rule); the demo's FA-offset DOLLARS are not ported — no
 * rate, cost or money key is read here, so the table renders for every reader. Lines
 * resolving to NO code roll into a disclosed "(uncoded)" residual row, always LAST — the
 * Equip Summary rule, never a silent drop. `codeById` labels line-level code ids (the list
 * payload carries only the HEADER's code string); header-learned labels back it up, and an
 * unlabelled id renders raw rather than hiding the bucket. */
export function buildCodeBurnRows(
  tickets: FieldTicket[], codeById: Map<string, string> = new Map(),
): (string | number)[][] {
  const dates = Array.from(new Set(tickets.map((t) => t.ticket_date))).sort();
  const UNCODED = '__uncoded__';
  const headerLabels = new Map<string, string>();
  const byCode = new Map<string, Map<string, number>>();
  for (const ticket of tickets) {
    if (ticket.project_cost_code_id && ticket.cost_code) {
      headerLabels.set(ticket.project_cost_code_id, ticket.cost_code);
    }
    for (const line of ticket.lines) {
      const key = line.project_cost_code_id ?? ticket.project_cost_code_id ?? UNCODED;
      const perDay = byCode.get(key) ?? new Map<string, number>();
      const hours = (num(line.qty) ?? 0) * (num(line.hours) ?? 0);
      perDay.set(ticket.ticket_date, (perDay.get(ticket.ticket_date) ?? 0) + hours);
      byCode.set(key, perDay);
    }
  }
  const label = (key: string) => (key === UNCODED
    ? '(uncoded)' : codeById.get(key) ?? headerLabels.get(key) ?? key);
  const header = ['COST CODE', ...dates, 'Total hrs'];
  const entries = Array.from(byCode.entries())
    // codes sorted by label; the uncoded residual always LAST — it is the disclosure line
    .sort(([ka], [kb]) => (ka === UNCODED ? 1 : kb === UNCODED ? -1
      : label(ka).localeCompare(label(kb))));
  const rows = entries.map(([key, perDay]) => {
    const cells = dates.map((d) => perDay.get(d) ?? 0);
    return [label(key), ...cells, cells.reduce((s, v) => s + v, 0)];
  });
  const totals = dates.map((_, i) => rows.reduce((s, r) => s + Number(r[i + 1] || 0), 0));
  return [header, ...rows, ['TOTALS', ...totals, totals.reduce((s, v) => s + v, 0)]];
}

const FieldTimesheetTab = ({ matterid }: FieldTimesheetTabProps): JSX.Element => {
  const financialVisible = useFinancialVisible();
  const capabilities = useBidCapabilities();
  // Cosmetic gate over the server's RECORD_PRODUCTION check — without it, a read-only role
  // (executive) sees a form whose only possible outcome is a 403. The WIRE value is lowercase
  // (roles.py: RECORD_PRODUCTION = 'record_production') — the constant's NAME is not on the wire.
  const mayRecord = capabilities?.capabilities.includes('record_production') ?? false;
  // Verify Day (gap 2.10): MUTATE_BIDS — the PM's authority, which the superintendent
  // deliberately lacks. Cosmetic over the server's gate, like mayRecord.
  const mayVerify = capabilities?.capabilities.includes('mutate_bids') ?? false;
  const queryClient = useQueryClient();

  const tickets = useQuery({
    queryKey: ['FieldTickets', matterid],
    queryFn: () => PipelineProjectService.getFieldTickets(matterid),
  });
  const phases = useQuery({
    queryKey: ['ProjectPhases', matterid],
    queryFn: () => PipelineProjectService.getPhases(matterid),
  });
  const costCodes = useQuery({
    queryKey: ['ProjectCostCodes', matterid],
    queryFn: () => PipelineProjectService.getCostCodes(matterid),
  });
  const labourOptions = useCatalogOptions('labour');
  const equipmentOptions = useCatalogOptions('equipment');
  // The fleet registry (gap 3.3): the project's bid-scoped units + the tenant-wide pool.
  const inventory = useQuery({
    queryKey: ['ProjectInventory', matterid],
    queryFn: () => PipelineProjectService.getInventory(matterid),
  });
  // The MSA cost-plus percentage (Tier 4.2): param-driven when configured; the literal below
  // otherwise — which is DELIBERATE (the plan's recorded exception): the chip documents the
  // MSA paper clause, not a computation.
  const configDefaults = useQuery({
    queryKey: ['BidConfigDefaults'],
    queryFn: () => listCatalog('/bid-config-defaults', true),
  });
  const costPlusPct = useMemo(() => {
    const param = (configDefaults.data ?? []).find(
      (p: any) => p.param_key === 'costPlusPct');
    const value = param?.param_value != null ? Number(param.param_value) : NaN;
    return Number.isFinite(value) && value >= 0 ? value : null;
  }, [configDefaults.data]);
  // The overload advisory's PLANNED half (validation set, decision f): the phase's
  // planned_headcount off the crew-loading endpoint — day-independent bid quantities; the
  // ACTUAL half is computed from the form day's tickets below, NEVER the endpoint's
  // actual_headcount (that is the LATEST recorded day, and an as-of mismatch would flag
  // today's crew against yesterday's count — the plan's named hazard). Same query key as
  // the Commercial tab, so the cache is shared.
  const crewLoading = useQuery({
    queryKey: ['CrewLoading', matterid],
    queryFn: () => PipelineProjectService.getCrewLoading(matterid),
  });
  // The roster the fan-out reads. Money-free and `can_access_matter`-gated already, and the key
  // is deliberately the one the Team tab writes through, so assigning a person there refreshes
  // the picker here for free.
  // Active sites only: this is a picker, and a retired site must not take new hours (the
  // service refuses one anyway, so offering it would only produce a save that fails).
  const sites = useQuery({
    queryKey: ['ProjectSites', matterid, 'active'],
    queryFn: () => SiteService.getSitesForMatter(matterid),
  });
  const absenceReasons = useQuery({
    queryKey: ['AbsenceReasons'],
    queryFn: () => PersonDayService.listAbsenceReasons(true),
  });
  const phaseCrew = useQuery({
    queryKey: ['ProjectPhaseCrew', matterid],
    queryFn: () => PipelineProjectService.getPhaseCrew(matterid),
  });
  const verifiedDays = useQuery({
    queryKey: ['VerifiedDays', matterid],
    queryFn: () => PipelineProjectService.getVerifiedDays(matterid),
  });
  const verifiedSet = useMemo(
    () => new Set((verifiedDays.data ?? []).map((d) => d.day)), [verifiedDays.data]);
  const [verifyDate, setVerifyDate] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const verify = useMutation({
    mutationFn: ({ day, on }: { day: string; on: boolean }) => (on
      ? PipelineProjectService.verifyDay(matterid, day)
      : PipelineProjectService.unverifyDay(matterid, day)),
    onSuccess: () => {
      setVerifyError(null);
      setVerifyDate('');
      void queryClient.invalidateQueries({ queryKey: ['VerifiedDays', matterid] });
    },
    onError: (err: any) => setVerifyError(err?.message ?? 'Could not change the verification.'),
  });

  const [form, setForm] = useState({ ...EMPTY_FORM });
  // Edit-until-verified (residual gap A3): one form, two modes. null = create; a ticket id =
  // the day being re-authored — submit becomes the PUT and the server re-runs every creation
  // wall (so a catalog re-price or a newly-landed wall, like A8's miss reason, applies to the
  // edit; its 400 surfaces verbatim below, never special-cased).
  const [editingId, setEditingId] = useState<string | null>(null);
  // The miss gate at DAY grain (validation set, decision d): the picked phase's planned
  // rate, from the /phases payload already fetched — no new fetch. NaN or 0 reads as NO
  // RATE RECORDED, not a zero plan (the column defaults to 0; absence ≠ 0), so no gate is
  // minted from a default; null = ungated (FA days exempt, phaseless, unrated).
  const planRate = useMemo(() => {
    if (form.is_force_account || !form.project_phase_id) return null;
    const phase = (phases.data ?? []).find((p) => p.id === form.project_phase_id);
    const rate = Number(phase?.production_per_day_m);
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  }, [phases.data, form.is_force_account, form.project_phase_id]);
  // The day-sum: the fetched tickets on the form's (date, phase) — excluding FA rows (the
  // bid-scope compare) and the ticket under edit (its stored value is being replaced by the
  // form's) — plus the form's own value. Mirrors the server's asymmetric rule (b): a reason
  // is DEMANDED when the day-sum misses, PERMITTED while this ticket is short on its own (a
  // split ticket's entry-time reason stays legal after a sibling cures the day), refused
  // otherwise — so the select reveals exactly when the server would accept one. The SERVER
  // stays authoritative: it sums Decimals where this sums floats, and an exact-rate
  // boundary can disagree — its refusal surfaces in the wired form error path.
  const dayMissed = useMemo(() => {
    if (planRate === null) return false;
    const siblings = (tickets.data ?? [])
      .filter((t) => t.ticket_date === form.ticket_date
        && t.project_phase_id === form.project_phase_id
        && !t.is_force_account && t.id !== editingId)
      .reduce((sum, t) => sum + (num(t.production_m) ?? 0), 0);
    return siblings + (num(form.production_m) ?? 0) < planRate;
  }, [tickets.data, planRate, form.ticket_date, form.project_phase_id,
    form.production_m, editingId]);
  // The PERMIT half: dayMissed implies ownShort (the day-sum can never be below the rate
  // while this ticket alone meets it), so ownShort is also "the select renders at all".
  const ownShort = planRate !== null && (num(form.production_m) ?? 0) < planRate;
  // Mirrors the server's walls so the submit blocks instead of 400ing: the demand blocks a
  // reasonless day; a PERMITTED 'other' pick still owes its note before it can ride.
  const missReasonIncomplete = (dayMissed && !form.production_miss_reason)
    || (ownShort && form.production_miss_reason === 'other' && !form.production_miss_note);
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }]);
  // R5: whether the author TOUCHED the day-level site/basis controls this session. Touched is
  // an explicit day-level decision and applies to every labour line (the original behaviour);
  // untouched, each edited line keeps its own stored value and the day value fills only the
  // lines that carry none — so an unrelated edit cannot homogenize per-line disagreement.
  const [siteTouched, setSiteTouched] = useState(false);
  const [basisTouched, setBasisTouched] = useState(false);
  // R1: the shift-start field posts on blur ONLY when the author actually changed it. A ref,
  // not state — the blur handler needs the current value and nothing renders from it.
  const shiftStartDirty = useRef(false);
  const [invoices, setInvoices] = useState<
    { poNumber: string; vendor: string; description: string; value: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  // The status bars' rows (decision e), keyed for the ticket-row lookup — derived from the
  // SAME fetched list and /phases payload as everything else here, no new fetch.
  const dayStatus = useMemo(() => new Map(
    buildDayProductionStatus(tickets.data ?? [], phases.data ?? [])
      .map((row) => [`${row.date}|${row.phaseId}`, row])), [tickets.data, phases.data]);
  // Overload = crew_loading's real meaning surfaced at entry (decision f): the form day's
  // hands on the picked phase — Σ labour-line qty across that day's tickets (FA INCLUDED:
  // presence on site is fact whatever the work's attribution, crew_loading's own rule; the
  // ticket under edit excluded — the form's lines stand in for it) plus the form's own
  // picked labour lines — against the phase's planned_headcount. Planned 0 → no flag (a
  // nag from a phase with no planned labour is a flag from a default). ADVISORY only,
  // never a wall: presence on site is fact, and headcounts are money-free.
  const overload = useMemo(() => {
    if (!form.project_phase_id || !form.ticket_date) return null;
    const planned = Number((crewLoading.data?.crews ?? [])
      .find((c) => c.phase_id === form.project_phase_id)?.planned_headcount);
    if (!Number.isFinite(planned) || planned <= 0) return null;
    const recorded = (tickets.data ?? [])
      .filter((t) => t.ticket_date === form.ticket_date
        && t.project_phase_id === form.project_phase_id && t.id !== editingId)
      .reduce((sum, t) => sum + countHands(t.lines), 0);
    const drafted = countHands(lines
      .filter((l) => l.kind === 'labour' && l.catalogId)
      .map((l) => ({ kind: 'labour', qty: l.qty === '' ? 1 : l.qty,
                     employeeid: l.employeeId || null, hour_type_entered: l.hourType || null })));
    const hands = recorded + drafted;
    return crewStatus(planned, hands) === 'Over' ? { hands, planned } : null;
  }, [crewLoading.data, tickets.data, form.project_phase_id, form.ticket_date,
    lines, editingId]);
  // The phase-window advisory for the FORM (decision g) — ticket rows wear the same check
  // per row below.
  const formOffSchedule = outsidePhaseWindow(
    form.ticket_date, form.project_phase_id || null, phases.data ?? []);

  const create = useMutation({
    mutationFn: () => {
      // Partial invoice rows REFUSE, they don't silently vanish: the first version filtered
      // vendorless rows off the wire and onSuccess destroyed the typed PO and value with no
      // feedback — the exact silent-drop failure this codebase names repeatedly (review).
      // Only a fully-empty row is dropped (an accidental "Add" click is not data).
      const partial = invoices.find((i) =>
        (!i.vendor && (i.poNumber || i.description || i.value))
        || (i.vendor && i.value === ''));
      if (partial) {
        return Promise.reject(new Error(!partial.vendor
          ? 'A third-party invoice needs a vendor — or clear the row to drop it.'
          : 'A third-party invoice needs a value — enter 0 only if the bill really is $0.'));
      }
      // Same doctrine for LINES (review): the catalog filter below treats pick-less rows as
      // blank, which was fine while blank rows could carry nothing — but a unit link, meter
      // readings or typed hours are DATA, and dropping them silently on success is the exact
      // failure the invoice rows already refuse. A truly-empty row (the default Add) still
      // drops as noise.
      const partialLine = lines.find((l) => !l.catalogId
        && (l.hours !== '' || l.unitId || l.meterStart !== '' || l.meterEnd !== ''
          || l.regHours !== '' || l.travHours !== ''
          // P7a: a row that NAMES somebody is data too. The fan-out leaves `catalogId` blank
          // whenever the roster's `role_key` does not resolve to a catalog role (it is NULL by
          // design, or a `label:` slug) — precisely the case the plan says must demand an
          // explicit pick. Without this clause those people's rows would be filtered out on
          // save and their day would vanish with no error.
          || l.employeeId));
      if (partialLine) {
        return Promise.reject(new Error(partialLine.employeeId
          ? `${partialLine.description || 'Someone'} has no role selected — pick one, or clear `
            + 'the row to leave them off this day.'
          : 'A line with hours, a unit or meter readings needs a catalog pick — or clear the row to drop it.'));
      }
      // Hours are never invented: the fan-out deliberately leaves them blank because no source
      // for a standard shift exists until a work agreement records one. So a named person with
      // a role but no hours is an unfinished row, not a zero-hour day.
      const hourlessPerson = lines.find((l) => l.employeeId && l.catalogId && l.hours === '');
      if (hourlessPerson) {
        return Promise.reject(new Error(
          `${hourlessPerson.description || 'Someone'} has no hours — enter them, or clear the row `
          + 'to leave them off this day.'));
      }
      const body: CreateFieldTicketBody = {
        ticket_date: form.ticket_date,
        project_phase_id: form.project_phase_id || null,
        project_cost_code_id: form.project_cost_code_id || null,
        weather: form.weather || undefined,
        ...(form.temp_high_c !== '' ? { temp_high_c: num(form.temp_high_c) } : {}),
        ...(form.temp_low_c !== '' ? { temp_low_c: num(form.temp_low_c) } : {}),
        ...(form.contractor_rep ? { contractor_rep: form.contractor_rep } : {}),
        ...(form.customer_rep ? { customer_rep: form.customer_rep } : {}),
        // The replace is WHOLESALE, so an edit that never carried notes would null a
        // recorded one silently (review) — the form now carries them, both modes.
        ...(form.notes ? { notes: form.notes } : {}),
        ...(invoices.some((i) => i.vendor) ? {
          invoices: invoices.filter((i) => i.vendor).map((i) => ({

            ...(i.poNumber ? { po_number: i.poNumber } : {}),
            vendor: i.vendor,
            ...(i.description ? { description: i.description } : {}),
            invoice_value: num(i.value) ?? 0,
          })),
        } : {}),
        production_m: num(form.production_m) ?? 0,
        man_hours: num(form.man_hours) ?? 0,
        equipment_hours: num(form.equipment_hours) ?? 0,
        // The FA marker is a flag+reason PAIR; the server refuses one without the other.
        is_force_account: form.is_force_account,
        ...(form.is_force_account ? { reason_code: form.reason_code } : {}),
        // The miss pair rides whenever the server would PERMIT it — this ticket short on
        // its own (rule b; the belt over the gate's hide: the server refuses a reason on a
        // ticket at/above the rate). A cured split ticket's stored reason must keep riding
        // its edits, or the wholesale replace would destroy it silently (the named
        // failure). The note rides ANY sent reason when present — typed only for `other`,
        // but an edited day may carry one beside another reason (legal server-side). It
        // renders whenever it rides, so nothing hidden is sent.
        ...(ownShort && form.production_miss_reason ? {
          production_miss_reason: form.production_miss_reason,
          ...(form.production_miss_note
            ? { production_miss_note: form.production_miss_note } : {}),
        } : {}),
        lines: lines
          .filter((l) => l.catalogId)
          .map((l): CreateTicketLine => {
            // R5: the line's own stored site/basis survive an edit; the day-level value fills
            // only lines that carry none — unless the author TOUCHED the control this session,
            // which is an explicit day-level decision and applies to every labour line. On a
            // fresh entry lines carry nothing, so the day-level value applies as it always did.
            const lineSite = siteTouched ? effectiveSiteId : (l.siteId || effectiveSiteId);
            const lineBasis = basisTouched
              ? form.billing_basis_field : (l.basis || form.billing_basis_field);
            // THE REASON FILLS, IT DOES NOT OVERWRITE — and this is the one place the day-level
            // controls do NOT all behave alike. Site and basis apply the author's day-level
            // decision to every line, because a crew works one spread and one commercial basis.
            // A5 deliberately made TWO REASONS ON ONE DAY representable, and any rule that pushes
            // the day-level value onto a line that already states its own destroys the second on
            // the first edit — reachable in two clicks through the force-account checkbox alone.
            // So a line keeps its own reason, and the day-level field supplies the ones that have
            // none. Changing a stated per-line reason is an API act until a per-line control
            // exists; blanking it here would be a worse trade.
            const lineReason = l.faReason || form.fa_reason_code;
            return {
            // R3: an edited line names the stored row it IS, so the server updates it in place
            // and the judgement columns other actors wrote survive the save. A fresh line sends
            // no id and inserts.
            ...(l.lineId ? { id: l.lineId } : {}),
            kind: l.kind,
            // The picked option's label, else the STORED description (edit mode): saving an
            // edit before the options query resolves must not degrade the name to ''.
            //
            // P7a (register F4): when the line names a PERSON the description becomes
            // "Role — Employee Name". The Long Sheet groups labour rows by description as
            // typed, so four qty-1 rows all reading "Labourer" would merge back into one
            // 40-hour row — the exact outcome the fan-out exists to prevent. Keeping the role
            // in front preserves the vocabulary the sheet already carries.
            description: (() => {
              const label = (l.kind === 'labour' ? labourOptions : equipmentOptions)
                .find((o) => o.id === l.catalogId)?.label ?? (l.description || undefined);
              if (l.kind !== 'labour' || !l.employeeId) return label;
              const person = (phaseCrew.data?.assignments ?? [])
                .find((a) => a.employeeid === l.employeeId)?.name;
              if (!person) return label;
              // An edit re-submits a description that already carries the name — do not
              // compose it twice.
              if (label && label.endsWith(`— ${person}`)) return label;
              return label ? `${label} — ${person}` : person;
            })(),
            ...(l.kind === 'labour'
              ? { labour_role_id: l.catalogId } : { equipment_item_id: l.catalogId }),
            // '' inherits the header's code server-side — send nothing, not an empty string.
            ...(l.costCodeId ? { project_cost_code_id: l.costCodeId } : {}),
            qty: num(l.qty) ?? 1,
            hours: num(l.hours) ?? 0,
            // '' = not split — omit, never send 0 (a zero split is a different fact).
            // Labour only, belt over the kind-flip reset: the server refuses a split on a
            // machine at every layer.
            // P7a: the person, and the axis they entered. Both omitted when unset, so a legacy
            // crew line goes over the wire exactly as it did before 0049.
            ...(l.kind === 'labour' && l.employeeId ? { employeeid: l.employeeId } : {}),
            // The columns are per-line because the schema supports disagreement, and the FORM
            // is per-day because a crew works one spread — resolved per line above (R5).
            // Omitted when unset — never sent as '' or as an invented default.
            ...(l.kind === 'labour' && lineSite ? { site_id: lineSite } : {}),
            ...(l.kind === 'labour' && lineBasis
              ? { billing_basis_field: lineBasis } : {}),
            // Sent only WITH a force-account basis — an orphan reason is refused by
            // `pftl_fa_reason_labour_only_check`'s sibling rules and says nothing on its own.
            ...(l.kind === 'labour' && lineBasis === 'force_account' && lineReason
              ? { fa_reason_code: lineReason } : {}),
            ...(l.kind === 'labour' && l.hourType ? { hour_type_entered: l.hourType } : {}),
            ...(l.kind === 'labour' && l.regHours !== '' ? { reg_hours: num(l.regHours) } : {}),
            ...(l.kind === 'labour' && l.travHours !== '' ? { trav_hours: num(l.travHours) } : {}),
            ...(l.kind === 'labour' && l.subsistence ? { subsistence: true } : {}),
            ...(l.kind === 'labour' && l.truck ? { truck: true } : {}),
            // Unit tracking (gap 3.3): equipment only, same belt-over-reset shape. '' = no
            // reading — omit, never 0 (a fresh meter's genuine 0.0 goes through as 0).
            ...(l.kind === 'equipment' && l.unitId ? { inventory_item_id: l.unitId } : {}),
            ...(l.kind === 'equipment' && l.meterStart !== ''
              ? { meter_start: num(l.meterStart) } : {}),
            ...(l.kind === 'equipment' && l.meterEnd !== ''
              ? { meter_end: num(l.meterEnd) } : {}),
            };
          }),
      };
      // One body, two verbs (A3): an edit is a wholesale replace of the same no-money shape.
      return editingId
        ? PipelineProjectService.replaceFieldTicket(matterid, editingId, body)
        : PipelineProjectService.createFieldTicket(matterid, body);
    },
    onSuccess: () => {
      setForm({ ...EMPTY_FORM });
      setLines([{ ...EMPTY_LINE }]);
      setInvoices([]);
      setEditingId(null);   // a successful edit exits edit mode
      setError(null);
      setSiteTouched(false);
      setBasisTouched(false);
      shiftStartDirty.current = false;
      void queryClient.invalidateQueries({ queryKey: ['FieldTickets', matterid] });
      void queryClient.invalidateQueries({ queryKey: ['BidVsActual', matterid] });
    },
    onError: (err: any) => setError(err?.message ?? 'Could not record the day.'),
  });

  /** Load an UNVERIFIED day into the form (A3): the pickers repopulate from the identity ids
   * the list now carries — labour_role_id / equipment_item_id are the load-bearing read fix;
   * without them every line's pick would silently drop, the named failure. */
  const startEdit = (t: FieldTicket) => {
    setEditingId(t.id);
    // R5: the day-level controls rehydrate honestly — the common value when every labour line
    // agrees, blank when they disagree (each line keeps its own through save), and the explicit
    // NO_SITE sentinel when no line carries a site, so the lone-active-site fallback cannot
    // fabricate a where-it-happened fact onto a deliberately siteless historical day.
    const labourLines = t.lines.filter((l) => l.kind === 'labour');
    const siteCarriers = labourLines.filter((l) => l.site_id);
    const siteValues = new Set(siteCarriers.map((l) => l.site_id));
    const basisCarriers = labourLines.filter((l) => l.billing_basis_field);
    const basisValues = new Set(basisCarriers.map((l) => l.billing_basis_field));
    // Against the FORCE-ACCOUNT carriers, not every basis carrier: `basisCarriers` counts
    // `standard` lines too, so a mixed day compared 1 reason against 2 bases and blanked a field
    // that agreed with itself.
    const faCarriers = labourLines.filter((l) => l.billing_basis_field === 'force_account');
    const reasonCarriers = faCarriers.filter((l) => l.fa_reason_code);
    const reasonValues = new Set(reasonCarriers.map((l) => l.fa_reason_code));
    setSiteTouched(false);
    setBasisTouched(false);
    shiftStartDirty.current = false;
    setForm({
      ticket_date: t.ticket_date,
      project_phase_id: t.project_phase_id ?? '',
      project_cost_code_id: t.project_cost_code_id ?? '',
      weather: t.weather ?? '',
      temp_high_c: t.temp_high_c ?? '',
      temp_low_c: t.temp_low_c ?? '',
      contractor_rep: t.contractor_rep ?? '',
      customer_rep: t.customer_rep ?? '',
      production_m: t.production_m,
      man_hours: t.man_hours,
      equipment_hours: t.equipment_hours,
      is_force_account: t.is_force_account,
      reason_code: t.reason_code ?? 'weather',
      production_miss_reason: t.production_miss_reason ?? '',
      production_miss_note: t.production_miss_note ?? '',
      notes: t.notes ?? '',
      site_id: siteCarriers.length === 0
        ? NO_SITE
        : (siteValues.size === 1 && siteCarriers.length === labourLines.length
          ? (siteCarriers[0].site_id as string) : ''),
      billing_basis_field:
        basisValues.size === 1 && basisCarriers.length === labourLines.length
          ? (basisCarriers[0].billing_basis_field as string) : '',
      // Same shape as the basis above and for the same reason: blank when the lines disagree,
      // because one field cannot honestly display two answers — and each line keeps its own
      // through the save.
      fa_reason_code:
        reasonValues.size === 1 && reasonCarriers.length === faCarriers.length
          ? (reasonCarriers[0].fa_reason_code as string) : '',
      shift_start: '',
    });
    // R1: seed the stored shift start so the field SHOWS what a blur would otherwise have
    // silently cleared. One-shot and display-only: it applies only while the author has not
    // touched the field and only if the form is still on this day; a failed read seeds
    // nothing and blocks nothing. Blank when the crew's recorded values disagree — a single
    // field cannot honestly display two answers.
    const crewIds = Array.from(new Set(
      t.lines.filter((l) => l.employeeid).map((l) => l.employeeid as string)));
    if (crewIds.length) {
      void PersonDayService.listDays(t.ticket_date, t.ticket_date).then((days) => {
        const crewDays = days.filter((d) => crewIds.includes(d.employeeid));
        const values = new Set(crewDays
          .filter((d) => d.shift_start_time)
          .map((d) => (d.shift_start_time as string).slice(0, 5)));
        if (values.size !== 1 || crewDays.some((d) => !d.shift_start_time)) return;
        const common = Array.from(values)[0];
        setForm((prev) => (prev.ticket_date === t.ticket_date && !shiftStartDirty.current
          ? { ...prev, shift_start: common } : prev));
      }).catch(() => { /* display-only seed */ });
    }
    setLines(t.lines.length ? t.lines.map((l): DraftLine => ({
      // R3: the stored identity rides the edit so the save UPDATES rather than re-minting —
      // which is what keeps the annotation, disposition and office basis on the row.
      lineId: l.id ?? '',
      kind: l.kind,
      catalogId: (l.kind === 'labour' ? l.labour_role_id : l.equipment_item_id) ?? '',
      description: l.description ?? '',
      costCodeId: l.project_cost_code_id ?? '',
      qty: l.qty,
      hours: l.hours,
      regHours: l.reg_hours ?? '',
      travHours: l.trav_hours ?? '',
      subsistence: !!l.subsistence,
      truck: !!l.truck,
      unitId: l.inventory_item_id ?? '',
      meterStart: l.meter_start ?? '',
      meterEnd: l.meter_end ?? '',
      // P7a, and load-bearing for the same reason the catalog ids are: without these an edit
      // would strip every person and every hour type off a fanned-out day, silently turning it
      // back into an unattributed crew record.
      employeeId: l.employeeid ?? '',
      hourType: l.hour_type_entered ?? '',
      // R5: the line's own stored site/basis survive the edit unless the author touches the
      // day-level control — see the submit's per-line resolution.
      siteId: l.site_id ?? '',
      basis: l.billing_basis_field ?? '',
      faReason: l.fa_reason_code ?? '',
    })) : [{ ...EMPTY_LINE }]);
    // invoice_value is MONEY and strips for a blind author, so it can load as '' here even
    // though the author typed it — the partial-invoice wall then REFUSES the submit until the
    // value is retyped from the paper. A refusal that says so, never a silent $0 (the
    // codebase's named doctrine; recorded in the plan's close-out).
    setInvoices((t.invoices ?? []).map((i) => ({
      poNumber: i.po_number ?? '',
      vendor: i.vendor,
      description: i.description ?? '',
      value: i.invoice_value ?? '',
    })));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setLines([{ ...EMPTY_LINE }]);
    setInvoices([]);
    setError(null);
    setSiteTouched(false);
    setBasisTouched(false);
    shiftStartDirty.current = false;
  };

  // The STYLED payroll pack (weaver-demo port, decided 2026-08-07): client-layout week
  // sheets via ExcelJS, plus the Summary (per-name Reg/Trav/OT grid, cross-foot column kept)
  // and EquipSummary sheets as plain grids. Lazily imported — the tab must not carry a
  // spreadsheet engine in its initial load. This retired `xlsx` from this file
  // (plans/todo/frontend-xlsx-removal.md, surface 1).
  const exportLongSheet = async () => {
    try {
      const ticketRows = tickets.data ?? [];
      const { exportLongSheetXlsx } = await import('./longSheetExport');
      // Counsel D4: the STATUTORY figure is what a payroll export must carry, and it cannot be
      // derived here — a block belongs to a LINE and a person-day's answer is a sum across their
      // lines. A failed read leaves the column BLANK rather than falling back to the legacy
      // residual: shipping a button that pays less than the entitlement is the documented
      // liability case, and a blank column says "not yet judged" honestly.
      let statutory: Map<string, { overtime: number; doubleTime: number }> | undefined;
      try {
        const days = ticketRows.map((t) => t.ticket_date).sort();
        statutory = statutoryPremiumByPerson(await PipelineProjectService.getClassifiedHours(
          matterid, days[0], days[days.length - 1]));
      } catch {
        statutory = undefined;
      }
      await exportLongSheetXlsx(
        ticketRows, matterid,
        buildLongSheetRows(ticketRows, statutory), buildEquipSummaryRows(ticketRows),
      );
    } catch {
      // A failed export must SAY so — the click handler is fire-and-forget, and an
      // unhandled rejection reads as "the button does nothing" (review).
      setError('Could not export the Long Sheet. Nothing was downloaded — try again.');
    }
  };

  /** Active sites for the picker. One site is a resolution, not an invention — see below. */
  const activeSites = useMemo(
    () => (sites.data ?? []).filter((site) => site.is_active),
    [sites.data],
  );

  /** The site this day records against: the author's pick, or the matter's ONE active site.
   *
   *  `NONE` is a real choice and has its own token. Deriving the fallback from an empty string
   *  made "(none recorded)" unpickable on a one-site matter — the select snapped back the moment
   *  it was chosen, so a siteless day could not be recorded at all. That is the same
   *  seeding-fights-the-author hazard `ProjectSitesTab` warns about, re-created in a derivation
   *  instead of an effect; a distinct sentinel is what keeps the pick winning.
   *
   *  ONE site preselects because that is a resolution. SEVERAL start blank, because "the first
   *  alphabetically" fabricates a fact about where a person stood — A7's argument one column over.
   */
  const effectiveSiteId = form.site_id === NO_SITE
    ? ''
    : form.site_id || (activeSites.length === 1 ? activeSites[0].id : '');

  /** The basis PROPOSED for this day (d.72), from the only source that actually exists.
   *
   *  d.72 says the basis inherits "the phase/cost-code default" — and THERE IS NO SUCH COLUMN.
   *  `default_billing_basis` appears on neither `project_phases` nor `project_cost_codes`
   *  anywhere in the migration chain, so the inheritance half of d.72 has nothing to read.
   *  Inventing one here would be DDL smuggled in behind a form.
   *
   *  What DOES exist is the ticket's own `is_force_account` flag, so ticking it SEEDS the field —
   *  visibly, into `form.billing_basis_field` — and the supervisor may then change it or clear it.
   *  The proposal is never applied behind the control: a value that submits differently from what
   *  the screen shows makes "the supervisor confirmed this" and "nobody looked" indistinguishable,
   *  which is the one thing d.72's propose-don't-decide shape exists to keep apart.
   *
   *  Note for 7b: A5 makes the header flag NON-authoritative once the basis lives on the line.
   *  Seeding from it is a starting value, never a synchronisation.
   */
  const seedBasisFromForceAccount = (isForceAccount: boolean) => {
    // Only seeds an UNSET field: a supervisor who already chose keeps their choice. And the
    // UNTICK takes its own seed back (review R16): a tick-then-untick otherwise left
    // 'force_account' standing as if somebody proposed it. Only the seed's own value is
    // cleared — an explicitly picked 'standard' (or anything else) survives the round trip.
    const basis = isForceAccount
      ? (form.billing_basis_field || 'force_account')
      : (form.billing_basis_field === 'force_account' ? '' : form.billing_basis_field);
    // Changing the day-level basis THROUGH the toggle is the same day-level decision as
    // touching the select (review D6): without this, unticking FA on an edited day showed
    // "(not stated)" while each line silently kept its stored force_account — the exact
    // shows-what-it-submits break this form's history is about.
    if (basis !== form.billing_basis_field) setBasisTouched(true);
    // The per-line reason seeds from the HEADER's reason on the same tick, and by the same rule:
    // only an UNSET field is seeded, and the untick takes its own seed back. A5 made the header
    // flag NON-authoritative once the basis lives on the line, so this is a starting value and
    // never a synchronisation — the two can legitimately differ, which is the whole reason the
    // column moved to the line. It is safe BECAUSE the submit fills rather than overwrites: a
    // line that already states a reason is untouched by whatever this seeds.
    const faReason = isForceAccount
      ? (form.fa_reason_code || form.reason_code)
      : (form.fa_reason_code === form.reason_code ? '' : form.fa_reason_code);
    setForm({ ...form, is_force_account: isForceAccount, billing_basis_field: basis,
      fa_reason_code: faReason });
  };

  /** Mark somebody absent for the day being recorded (register C2, TS7A-05).
   *
   *  An absence is a header with NO lines, so this removes the person from the draft rather than
   *  zeroing their hours — a zero-hour day is not an absence, and the calculator refuses
   *  non-positive hours by design. Both refusals are the server's and surface as sentences.
   */
  const markAbsent = useMutation({
    mutationFn: (vars: { employeeId: string; reasonId: string }) =>
      PersonDayService.recordAbsence({
        employeeid: vars.employeeId,
        work_date: form.ticket_date,
        ...(vars.reasonId ? { absence_reason_id: vars.reasonId } : {}),
      }),
    onSuccess: (_data, vars) => {
      setLines((prev) => prev.filter((l) => l.employeeId !== vars.employeeId));
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['FieldTickets', matterid] });
    },
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not record the absence.'),
  });

  /** One shift start for every person on the draft (A7 / D5).
   *
   *  Deduped before it is sent: a person holds TWO draft rows on a normal day — the working row
   *  and the travel row the entered axis creates — and the same id twice in one upsert raises
   *  "cannot affect row a second time". The server dedupes too; doing it here as well keeps the
   *  request honest about how many people it names.
   */
  const applyShiftStart = useMutation({
    mutationFn: (value: string) => {
      const people = Array.from(
        new Set(lines.map((l) => l.employeeId).filter(Boolean) as string[]));
      if (!people.length) {
        return Promise.reject(new Error('Add the crew before setting a shift start.'));
      }
      return PersonDayService.setDayShiftStart(
        people, form.ticket_date, value === '' ? null : value);
    },
    onSuccess: () => {
      setError(null);
      // The posted value is now the server's value — the field is clean again, so a later
      // untouched blur posts nothing (R1).
      shiftStartDirty.current = false;
    },
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not set the shift start.'),
  });

  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  /** The crew assigned to the phase being recorded, minus anyone already on the draft.
   *
   *  `on_team: false` assignments are skipped: they are people the phase plan names who are not
   *  actually on this project's team, and pricing a day for them is a guess. */
  const rosterForPhase = useMemo(() => {
    const assignments = phaseCrew.data?.assignments ?? [];
    const already = new Set(lines.map((l) => l.employeeId).filter(Boolean));
    return assignments.filter((a) => a.on_team
      && (!form.project_phase_id || a.project_phase_id === form.project_phase_id)
      && !already.has(a.employeeid));
  }, [phaseCrew.data, lines, form.project_phase_id]);

  /** Fan the phase roster out to one line per person (register A1).
   *
   *  At 41 people a superintendent picks a crew rather than typing a count, and every person
   *  needs their own row because a header keyed on (person, date) cannot attach to a four-hand
   *  line. Two rules the plan is explicit about, both visible here:
   *
   *   * HOURS ARE NOT DEFAULTED. There is no source for a standard shift until a work agreement
   *     records one, so an invented 8 would be a fabricated fact on a payroll document.
   *   * A ROLE THAT DOES NOT RESOLVE DEMANDS AN EXPLICIT PICK. `role_key` is a labour_roles id
   *     only when a line resolved the catalog; otherwise it is a `label:`-prefixed slug, and
   *     both that and NULL leave `catalogId` empty so the row cannot be submitted until somebody
   *     chooses — rather than pricing silently at zero.
   */
  const fanOutRoster = () => {
    if (!rosterForPhase.length) return;
    setLines((prev) => {
      const seeded = prev.filter((l) => l.catalogId || l.employeeId || l.hours);
      return [...seeded, ...rosterForPhase.map((person): DraftLine => ({
        ...EMPTY_LINE,
        kind: 'labour',
        employeeId: person.employeeid,
        description: person.name ?? '',
        catalogId: person.role_key && !person.role_key.startsWith('label:') ? person.role_key : '',
        qty: '1',
        hours: '',
        hourType: 'working',
      }))];
    });
  };

  /** Everyone with recorded hours on the ticket dates in view, one entry per (person, day).
   *
   *  Derived from the tickets already loaded rather than fetched: the approval grain IS the
   *  person-day, so the list of people to approve is exactly the set of people whose lines name
   *  them. A personless legacy crew line contributes nobody, which is correct — it has no
   *  person-day to approve.
   */
  const approvableCrew = useMemo(() => {
    const seen = new Map<string, { employeeid: string; name: string; day: string }>();
    const approved = new Set<string>();
    for (const ticket of tickets.data ?? []) {
      for (const line of ticket.lines) {
        if (line.kind !== 'labour' || !line.employeeid) continue;
        const key = `${line.employeeid}|${ticket.ticket_date}`;
        // R4: an APPROVED person-day leaves the window entirely. The cap exists to surface the
        // oldest UNAPPROVED days — approving a day used to leave its panel in place rendering
        // "approved", so once the 25 oldest pairs were approved every slot was an approved
        // panel forever and no unapproved day could be reached from the UI again. Any line of
        // the pair carrying `classified_at` marks the whole pair: the lines share one header.
        if (line.classified_at) approved.add(key);
        if (!seen.has(key)) {
          seen.set(key, {
            employeeid: line.employeeid,
            name: line.employee_name || line.description || 'Unnamed',
            day: ticket.ticket_date,
          });
        }
      }
    }
    // OLDEST FIRST, and the direction is the whole point. Approval is CONTIGUOUS — a day is
    // refused while any earlier day in its period carries unapproved hours — so the days that
    // must be approved first are the OLDEST ones. A newest-first cap hides exactly those: on a
    // twelve-person crew the newest 25 covers Wednesday and Thursday, Monday and Tuesday fall off
    // the screen, and every panel that renders refuses with no route to the ones that would
    // unblock them. The bound is still needed — each panel reads its own readiness on mount, and
    // `tickets` is fetched with no date window — but it must trim the TAIL, not the head.
    const all = Array.from(seen.values())
      .filter((person) => !approved.has(`${person.employeeid}|${person.day}`))
      .sort((a, b) => (a.day.localeCompare(b.day) || a.name.localeCompare(b.name)));
    return { shown: all.slice(0, APPROVAL_PANEL_LIMIT), total: all.length };
  }, [tickets.data]);

  /** Would any labour line on this draft be saved as force account, and does any of them lack a
   *  reason?
   *
   *  THE CONTROL CANNOT KEY ON THE HEADER BASIS. That field blanks whenever the lines disagree —
   *  which is exactly what a MIXED ticket looks like, one `force_account` line beside a `standard`
   *  one — so no reason select rendered, the per-line basis still resolved to force account, the
   *  payload omitted `fa_reason_code`, and every save 400'd with no control on screen to fix it.
   *  0050's own migration comment names supplying the reason as THE remedy for exactly these rows,
   *  and the entry surface is the only place a human can supply it.
   */
  const faDraftState = useMemo(() => {
    // The DAY-LEVEL basis is a trigger in its own right, not only through the lines: a supervisor
    // ticks force account before picking a role, and a control that waits for the first line to
    // exist is a control they meet after the mistake rather than before it. The per-line scan
    // below ADDS to this rather than replacing it — that is the whole of the mixed-ticket fix.
    let any = form.billing_basis_field === 'force_account';
    let missing = any && !form.fa_reason_code;
    for (const line of lines) {
      if (line.kind !== 'labour' || !line.catalogId) continue;
      const basis = basisTouched ? form.billing_basis_field : (line.basis || form.billing_basis_field);
      if (basis !== 'force_account') continue;
      any = true;
      const reason = line.faReason || form.fa_reason_code;
      if (!reason) missing = true;
    }
    return { any, missing };
  }, [lines, form.billing_basis_field, form.fa_reason_code, basisTouched]);

  const codeById = useMemo(
    () => new Map((costCodes.data ?? []).map((c) => [c.id, c.code])), [costCodes.data]);

  return (
    <div className="pipeline-schedule-grid">
      <section className="pipeline-panel">
        <div className="pipeline-panel-header">
          <h2 className="pipeline-panel-title">Field tickets</h2>
          {financialVisible && (
            <button
              className="pipeline-action-btn"
              onClick={() => void exportLongSheet()}
              disabled={!tickets.data?.length}
              // A greyed button with no reason reads as broken; the export needs days to export.
              title={tickets.data?.length ? undefined : 'Record a day first — there is nothing to export yet'}
            >
              Export payroll Long Sheet
            </button>
          )}
        </div>
        <p className="pipeline-panel-subtitle">
          One ticket per crew per day — production, hours and conditions.
        </p>
        {tickets.isLoading && <p className="pipeline-muted">Loading tickets…</p>}
        {tickets.isError && <p className="pipeline-muted">Could not load field tickets.</p>}
        {tickets.data?.length === 0 && (
          <p className="pipeline-muted">No days recorded yet.</p>
        )}
        {!!tickets.data?.length && (
          <table className="pipeline-table">
            <thead>
              <tr>
                <th>Date</th><th>Cost code</th><th>Weather</th><th>Production (m)</th>
                <th>Man hrs</th><th>Equip hrs</th><th>Lines</th>
              </tr>
            </thead>
            <tbody>
              {tickets.data.map((t) => {
                // The day's status bar (decision e): the (date, phase) pool's bid-scope
                // sum vs the rate — FA rows wear none (their metres sit outside the pool).
                const day = t.is_force_account
                  ? undefined : dayStatus.get(`${t.ticket_date}|${t.project_phase_id}`);
                const offSchedule = outsidePhaseWindow(
                  t.ticket_date, t.project_phase_id, phases.data ?? []);
                return (
                <tr key={t.id}>
                  <td>
                    {t.ticket_date}
                    {verifiedSet.has(t.ticket_date) && (
                      <span className="pipeline-verified-badge" title="Verified — day is locked">
                        ✓ Verified
                      </span>
                    )}
                    {t.is_force_account && (
                      <span className="pipeline-fa-badge" title={t.reason_code ?? undefined}>
                        FA{t.reason_code ? ` · ${t.reason_code}` : ''}
                      </span>
                    )}
                    {/* The phase-window advisory (decision g) — a fact flag, never a wall. */}
                    {offSchedule && (
                      <span
                        className="pipeline-window-badge"
                        title="This day falls outside the phase's planned window — advisory only"
                      >
                        Off schedule
                      </span>
                    )}
                    {/* Edit-until-verified (A3): the affordance exists ONLY while the day is
                        unverified — a verified row keeps its ✓ badge and offers nothing (the
                        server refuses anyway; this is the cosmetic gate over it, like
                        mayRecord). Gated on the author capability the form itself wears. */}
                    {mayRecord && !verifiedSet.has(t.ticket_date) && (
                      <button
                        type="button" className="pipeline-secondary-btn"
                        onClick={() => startEdit(t)}
                        disabled={create.isPending}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                  <td>{t.cost_code ?? codeById.get(t.project_cost_code_id ?? '') ?? '—'}</td>
                  <td>
                    {t.weather ?? '—'}
                    {(t.temp_high_c != null || t.temp_low_c != null) && (
                      <span className="pipeline-muted">
                        {' '}L {t.temp_low_c ?? '—'}° / H {t.temp_high_c ?? '—'}°
                      </span>
                    )}
                  </td>
                  <td>
                    {t.production_m}
                    {day && (
                      <span
                        className="pipeline-prod-track"
                        role="img"
                        aria-label={`day production ${day.status}`}
                        title={`Day: ${day.produced} of ${day.planned} m planned`}
                      >
                        <span
                          className={`pipeline-prod-fill pipeline-prod-${day.status}`}
                          style={{ width: `${Math.min(100, (day.produced / day.planned) * 100)}%` }}
                        />
                      </span>
                    )}
                  </td>
                  <td>{t.man_hours}</td>
                  <td>{t.equipment_hours}</td>
                  <td>
                    {(t.invoices ?? []).map((invoice, i) => (
                      <div key={`inv-${i}`} className="pipeline-line-cell">
                        {/* Cost + 10%: the MSA clause on every third-party bill — shown, not
                            applied here; billing-sheet integration is a recorded deferral. */}
                        3rd party: {invoice.vendor}
                        {invoice.po_number ? ` · ${invoice.po_number}` : ''}
                        {invoice.invoice_value !== undefined && (
                          <span className="pipeline-line-money">
                            {' '}· {money(num(invoice.invoice_value))}
                            {' '}(+{costPlusPct ?? 10}% MSA)
                          </span>
                        )}
                      </div>
                    ))}
                    {(t.contractor_rep || t.customer_rep) && (
                      <div className="pipeline-line-cell pipeline-muted">
                        Signed: {t.contractor_rep ?? '—'} / {t.customer_rep ?? '—'}
                      </div>
                    )}
                    {t.lines.map((line, i) => (
                      <div key={i} className="pipeline-line-cell">
                        {line.description || line.kind} · {line.qty} × {line.hours}h
                        {line.inventory_item_id && (
                          <span className="pipeline-line-code"> · {line.unit_tag ?? '(untagged)'}</span>
                        )}
                        {(line.meter_start != null || line.meter_end != null) && (
                          <span className="pipeline-muted">
                            {' '}· meter {line.meter_start ?? '—'}→{line.meter_end ?? '—'}
                          </span>
                        )}
                        {line.project_cost_code_id
                          && codeById.get(line.project_cost_code_id) && (
                          <span className="pipeline-line-code">
                            {' '}· {codeById.get(line.project_cost_code_id)}
                          </span>
                        )}
                        {line.line_total !== undefined && (
                          <span className="pipeline-line-money"> · {money(num(line.line_total))}</span>
                        )}
                      </div>
                    ))}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!!tickets.data?.some((t) => t.lines.some(
          (l) => l.kind === 'equipment' && l.inventory_item_id,
        )) && (
          <details className="pipeline-equip-summary">
            <summary>Equip summary — hours by unit</summary>
            {/* The day sheets' weekly per-unit block (gap 3.3), from the SAME rows the export
                writes. Quantities only — renders for every reader. */}
            <table className="pipeline-table">
              <tbody>
                {buildEquipSummaryRows(tickets.data ?? []).map((row, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    {row.map((cell, j) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={j} className={i === 0 ? 'pipeline-muted' : undefined}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
        {!!tickets.data?.some((t) => t.lines.length) && (
          <details className="pipeline-equip-summary">
            <summary>Daily burn — hours by cost code</summary>
            {/* The A10 table (the demo's burnByCode, money-free by decision): hours per code
                per day from the SAME fetched list. Quantities only — renders for every
                reader, no financial gating. */}
            <table className="pipeline-table">
              <tbody>
                {buildCodeBurnRows(tickets.data ?? [], codeById).map((row, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    {row.map((cell, j) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={j} className={i === 0 ? 'pipeline-muted' : undefined}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </section>

      {mayVerify && (
      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Verify a day</h2>
        <p className="pipeline-panel-subtitle">
          Sign a day off — a verified day refuses new field tickets and FA tickets until the
          verification is revoked. Both actions are audited.
        </p>
        <div className="pipeline-form-row">
          <label>
            Day
            <input
              type="date" value={verifyDate} aria-label="Day to verify"
              onChange={(e) => setVerifyDate(e.target.value)}
            />
          </label>
          <button
            type="button" className="pipeline-action-btn"
            disabled={!verifyDate || verify.isPending}
            onClick={() => verify.mutate({ day: verifyDate, on: true })}
          >
            Verify day
          </button>
        </div>
        {verifiedDays.isError && (
          // Silence here would read as "nothing verified" — the exact wrong conclusion for
          // the one screen deciding whether to sign a day off.
          <p className="pipeline-muted">Could not load the verified days.</p>
        )}
        {!!verifiedDays.data?.length && (
          <ul className="pipeline-verified-list">
            {verifiedDays.data.map((d) => (
              <li key={d.day}>
                {d.day} — verified
                <button
                  type="button" className="pipeline-secondary-btn"
                  onClick={() => verify.mutate({ day: d.day, on: false })}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
        {verifyError && <p className="pipeline-form-error">{verifyError}</p>}
      </section>
      )}

      {/* P7b: PAYROLL APPROVAL, which is a different act from the verification above.
          Verifying a day is the COMMERCIAL sign-off — a PM locking hours and costs for their
          matter. Approving a PERSON-DAY is the statutory one, and its readiness is DERIVED from
          whether every project-day that person's lines touch is verified. The two tiers sit
          together here because the second is unreachable until the first is done, and an
          approver who cannot see why would have nothing to act on. */}
      {mayVerify && !!approvableCrew.shown.length && (
      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Approve the day for payroll</h2>
        <p className="pipeline-panel-subtitle">
          One person, one day. The calculator runs from the start of that person&rsquo;s active
          period through this day and writes only this day&rsquo;s hours &mdash; the weekly
          threshold is a running count, so a later day cannot be approved before an earlier one.
        </p>
        {approvableCrew.total > approvableCrew.shown.length && (
          // Counted against the UNTRUNCATED total, so the notice says how many are hidden rather
          // than firing at exactly the limit with nothing behind it.
          <p className="pipeline-muted">
            Showing the {approvableCrew.shown.length} oldest unapproved person-days of{' '}
            {approvableCrew.total}. Approval is contiguous, so these are the ones that unblock the
            rest — the remainder appear as these are approved.
          </p>
        )}
        {approvableCrew.shown.map((person) => (
          <PersonDayApprovalPanel
            key={`${person.employeeid}-${person.day}`}
            employeeid={person.employeeid}
            employeeName={person.name}
            workDate={person.day}
          />
        ))}
      </section>
      )}

      {mayRecord && (
      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">
          {editingId ? 'Edit day' : 'Record a day'}
        </h2>
        <p className="pipeline-panel-subtitle">
          {editingId
            // The edit RE-AUTHORS the day (decision b): current catalog rules apply, and the
            // server re-runs every creation wall — say what saving means.
            ? 'Editing a recorded day — saving replaces it wholesale and re-prices from the current catalogs.'
            : 'Pick from the catalogs and enter hours — pricing is resolved and frozen by the server.'}
        </p>
        <form
          className="pipeline-form"
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        >
          <div className="pipeline-form-row">
            <label>
              Date
              <input
                type="date" required value={form.ticket_date}
                onChange={(e) => setForm({ ...form, ticket_date: e.target.value })}
              />
            </label>
            {/* A7 / D5: nullable with NO default, and no placeholder either. Blank means day-grain
                 classification on the recorded date — a real answer. An invented 07:00 would make an
                 unfilled night shift indistinguishable from a day shift. Applies to everyone on the
                 draft in one call, because at 41 people per-person would be 41 round trips. */}
            <label>
              Shift start
              <input
                type="time"
                value={form.shift_start}
                onChange={(e) => {
                  shiftStartDirty.current = true;
                  setForm({ ...form, shift_start: e.target.value });
                }}
                // Posts ONLY what the author actually changed (R1): an untouched blur posts
                // nothing, because '' → NULL is a real clear server-side and an uncontrolled
                // field erased the crew's recorded shift start on a mere tab-through.
                onBlur={() => {
                  if (form.ticket_date && shiftStartDirty.current) {
                    applyShiftStart.mutate(form.shift_start);
                  }
                }}
                disabled={!form.ticket_date || applyShiftStart.isPending}
                title={form.ticket_date
                  ? 'Applies to everyone on this day. Leave blank if the shift start is not recorded.'
                  : 'Pick a date first'}
              />
            </label>
            {/* W4b: WHERE the labour happened — what picks the law when P7b classifies (d.14). Blank
                 when the matter has several sites and nobody has chosen; the day cannot be recorded
                 against a site nobody stood on. */}
            <label>
              Site
              <select
                value={effectiveSiteId || (form.site_id === NO_SITE ? NO_SITE : '')}
                onChange={(e) => {
                  // Touching this control is an explicit day-level decision: the pick applies
                  // to every labour line on save, carried per-line values included (R5).
                  setSiteTouched(true);
                  setForm({
                    ...form,
                    // An explicit blank is a CHOICE and is stored as one, so the single-site
                    // resolution cannot overwrite it on the next render.
                    site_id: e.target.value === '' ? NO_SITE : e.target.value,
                  });
                }}
              >
                <option value={NO_SITE}>(none recorded)</option>
                {activeSites.map((site) => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
            </label>
            {/* W4c: the basis PROPOSED at entry (d.72), supervisor-overridable. Unset stays unset —
                 "nobody stated a basis" is a true answer, and better than a `standard` nobody chose. */}
            <label>
              Billing basis
              <select
                value={form.billing_basis_field}
                onChange={(e) => {
                  // Same day-level-decision rule as the site pick (R5).
                  setBasisTouched(true);
                  setForm({ ...form, billing_basis_field: e.target.value });
                }}
              >
                <option value="">(not stated)</option>
                <option value="standard">Standard</option>
                <option value="force_account">Force account</option>
              </select>
            </label>
            {/* P7b: REQUIRED when the basis is force account — 0050's `pftl_fa_reason_required_check`
                 refuses the line without it, and the reason is what decides whether the hours are
                 chargeable to the client. The control appears only when it can mean something. */}
            {faDraftState.any && (
              <label>
                FA reason (line)
                <select
                  // Required only when a force-account line would be saved WITHOUT one. A day
                  // whose lines already carry their own reasons — two of them, which A5 made
                  // representable — must not be blocked by a blank day-level field.
                  required={faDraftState.missing}
                  value={form.fa_reason_code}
                  onChange={(e) => setForm({ ...form, fa_reason_code: e.target.value })}
                >
                  <option value="">Pick a reason…</option>
                  {FA_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Phase
              <select
                value={form.project_phase_id}
                onChange={(e) => setForm({ ...form, project_phase_id: e.target.value })}
              >
                <option value="">—</option>
                {(phases.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
            <label>
              Cost code
              <select
                value={form.project_cost_code_id}
                onChange={(e) => setForm({ ...form, project_cost_code_id: e.target.value })}
              >
                <option value="">—</option>
                {(costCodes.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </select>
            </label>
            <label>
              Weather
              <input
                value={form.weather} placeholder="e.g. Rain"
                onChange={(e) => setForm({ ...form, weather: e.target.value })}
              />
            </label>
            <label>
              Temp high (°C)
              <input
                type="number" value={form.temp_high_c}
                onChange={(e) => setForm({ ...form, temp_high_c: e.target.value })}
              />
            </label>
            <label>
              Temp low (°C)
              <input
                type="number" value={form.temp_low_c}
                onChange={(e) => setForm({ ...form, temp_low_c: e.target.value })}
              />
            </label>
          </div>
          <div className="pipeline-form-row">
            <label>
              Production (m)
              <input
                type="number" min="0" value={form.production_m}
                onChange={(e) => setForm({ ...form, production_m: e.target.value })}
              />
            </label>
            <label>
              Man hours
              <input
                type="number" min="0" value={form.man_hours}
                onChange={(e) => setForm({ ...form, man_hours: e.target.value })}
              />
            </label>
            <label>
              Equipment hours
              <input
                type="number" min="0" value={form.equipment_hours}
                onChange={(e) => setForm({ ...form, equipment_hours: e.target.value })}
              />
            </label>
          </div>
          <div className="pipeline-form-row">
            <label className="pipeline-check-label">
              <input
                type="checkbox" checked={form.is_force_account}
                onChange={(e) => seedBasisFromForceAccount(e.target.checked)}
              />
              Out-of-scope / force-account day
            </label>
            {form.is_force_account && (
              <label>
                FA reason
                <select
                  value={form.reason_code}
                  onChange={(e) => setForm({ ...form, reason_code: e.target.value })}
                >
                  {FA_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
              </label>
            )}
            {/* The miss gate (the FA-reason pattern extended, day-sum grained): a bid-scope
                DAY below the picked phase's planned rate must say why before it can submit
                (required); a ticket short on its OWN while the day meets plan may still say
                why (optional — the server's permit rule, so a cured split ticket's stored
                reason survives its edits). Never renders for FA days, phaseless days, or
                unrated phases — the server holds the same walls. */}
            {ownShort && (
              <label>
                Miss reason
                <select
                  required={dayMissed} value={form.production_miss_reason}
                  onChange={(e) => setForm({ ...form, production_miss_reason: e.target.value })}
                >
                  <option value="">
                    {dayMissed ? 'Below plan — pick a reason…' : 'Day met by others — optional…'}
                  </option>
                  {MISS_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
              </label>
            )}
            {/* Renders whenever the note would ride the wire (`other` demands one; an edited
                day may carry one beside another reason) — nothing hidden is ever sent. */}
            {ownShort && (form.production_miss_reason === 'other'
              || form.production_miss_note !== '') && (
              <label>
                Miss note
                <input
                  required={form.production_miss_reason === 'other'}
                  value={form.production_miss_note}
                  placeholder="What happened"
                  onChange={(e) => setForm({ ...form, production_miss_note: e.target.value })}
                />
              </label>
            )}
          </div>
          {/* Entry-time advisories (decisions f/g): flags, never walls — neither blocks
              submit, and both are money-free (hands and dates only). */}
          {overload && (
            <p className="pipeline-advisory">
              Overload: {overload.hands} hands on this phase for {form.ticket_date} — the
              plan carries {overload.planned}. Advisory only; presence on site is a fact.
            </p>
          )}
          {formOffSchedule && (
            <p className="pipeline-advisory">
              Off schedule: {form.ticket_date} is outside the picked phase&apos;s planned
              window. Advisory only — work off schedule is a fact worth recording.
            </p>
          )}

          {lines.map((line, i) => (
            <div className="pipeline-form-row pipeline-line-row" key={i}>
              <label>
                Kind
                <select
                  value={line.kind}
                  onChange={(e) => setLine(i, {
                    // Reset the per-person fields with the kind: flipping to Equipment HID
                    // the split inputs but kept their values, and the submit 400d on fields
                    // the user could no longer see (review, proven through the shipped UI).
                    // The unit fields reset for the same reason in the other direction.
                    kind: e.target.value as DraftLine['kind'], catalogId: '', description: '',
                    regHours: '', travHours: '', subsistence: false, truck: false,
                    unitId: '', meterStart: '', meterEnd: '' })}
                >
                  <option value="labour">Labour</option>
                  <option value="equipment">Equipment</option>
                </select>
              </label>
              <label>
                {line.kind === 'labour' ? 'Role' : 'Equipment'}
                <select
                  value={line.catalogId}
                  onChange={(e) => setLine(i, { catalogId: e.target.value })}
                >
                  <option value="">Pick from catalog…</option>
                  {(line.kind === 'labour' ? labourOptions : equipmentOptions).map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Line cost code
                <select
                  value={line.costCodeId}
                  onChange={(e) => setLine(i, { costCodeId: e.target.value })}
                >
                  <option value="">Ticket default</option>
                  {(costCodes.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Qty
                <input
                  type="number" min="0" value={line.qty}
                  // A unit-linked line records ONE unit — the server refuses any other qty,
                  // so the state is simply not constructible here (the award-radio idiom).
                  disabled={line.kind === 'equipment' && !!line.unitId}
                  onChange={(e) => setLine(i, { qty: e.target.value })}
                />
              </label>
              <label>
                Hours
                <input
                  type="number" min="0" value={line.hours}
                  onChange={(e) => setLine(i, { hours: e.target.value })}
                />
              </label>
              {line.kind === 'equipment' && (
                <>
                  {/* Unit tracking (gap 3.3) — machines get unit numbers and hour meters,
                      the payroll split's mirror image. Picking a unit locks qty to 1. */}
                  <label>
                    Unit
                    <select
                      value={line.unitId}
                      onChange={(e) => setLine(i, {
                        unitId: e.target.value,
                        ...(e.target.value ? { qty: '1' } : {}),
                      })}
                    >
                      <option value="">No unit</option>
                      {(inventory.data ?? []).map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.asset_tag ? `${u.asset_tag} · ` : ''}{u.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Meter start
                    <input
                      type="number" min="0" value={line.meterStart}
                      onChange={(e) => setLine(i, { meterStart: e.target.value })}
                    />
                  </label>
                  <label>
                    Meter end
                    <input
                      type="number" min="0" value={line.meterEnd}
                      onChange={(e) => setLine(i, { meterEnd: e.target.value })}
                    />
                  </label>
                </>
              )}
              {line.kind === 'labour' && line.employeeId && (
                /* TS7A-05 from the surface that knows who is on the crew. An absence is a header
                   with NO lines, so this removes the person from the draft rather than zeroing
                   their hours — a zero-hour day is not an absence, and the calculator refuses
                   non-positive hours by design. The server owns both refusals (a day with hours
                   refuses the absence; an absent day refuses hours) and they surface here as
                   sentences. */
                <label className="pipeline-check-label">
                  Absent
                  <select
                    value=""
                    disabled={!form.ticket_date || markAbsent.isPending}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      markAbsent.mutate({
                        employeeId: line.employeeId,
                        reasonId: e.target.value === '__none__' ? '' : e.target.value,
                      });
                    }}
                    title={form.ticket_date
                      ? 'Record this person absent for the day'
                      : 'Pick a date first'}
                  >
                    <option value="">Mark absent…</option>
                    <option value="__none__">(reason not stated)</option>
                    {(absenceReasons.data ?? []).map((r) => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                </label>
              )}
              {line.kind === 'labour' && (
                <>
                  {/* P7a's ENTERED axis (0049). One hour type per row, so a person's travel is
                      its own line — which is why the Long Sheet keys on the person and the
                      headcount readers count distinct people rather than rows. Nothing here is
                      classified: what the statute calls regular is P7b's business. */}
                  <label>
                    Type
                    <select
                      value={line.hourType}
                      // Stating an hour type CLEARS the legacy split — the same reset the Kind
                      // flip does, for the same reason: the inputs hide below, and a submit
                      // carrying both axes 400s on values the user can no longer see (review
                      // R11). Un-stating the type reveals the (now empty) inputs for retyping.
                      onChange={(e) => setLine(i, e.target.value
                        ? { hourType: e.target.value, regHours: '', travHours: '' }
                        : { hourType: '' })}
                    >
                      <option value="">(not stated)</option>
                      <option value="working">Working</option>
                      <option value="travel">Travel</option>
                    </select>
                  </label>
                  {/* The legacy payroll split (gap 3.1/3.2), frozen historical-only at 0049.
                      Shown only while the row has NOT stated an hour type: the two describe the
                      same fact, and a row carrying both would print two different splits on the
                      two sheets — the server refuses it at both layers. Rows recorded before
                      0049 keep their inputs, so an edit never silently strips them. */}
                  {!line.hourType && (
                  <>
                  <label>
                    Reg
                    <input
                      type="number" min="0" value={line.regHours}
                      onChange={(e) => setLine(i, { regHours: e.target.value })}
                    />
                  </label>
                  <label>
                    Trav
                    <input
                      type="number" min="0" value={line.travHours}
                      onChange={(e) => setLine(i, { travHours: e.target.value })}
                    />
                  </label>
                  </>
                  )}
                  <label className="pipeline-check-label">
                    <input
                      type="checkbox" checked={line.subsistence}
                      onChange={(e) => setLine(i, { subsistence: e.target.checked })}
                    />
                    Sub
                  </label>
                  <label className="pipeline-check-label" title="Counts qty trucks — for 2 hands sharing 1 truck, record them as separate qty-1 lines">
                    <input
                      type="checkbox" checked={line.truck}
                      onChange={(e) => setLine(i, { truck: e.target.checked })}
                    />
                    Truck
                  </label>
                </>
              )}
            </div>
          ))}
          {invoices.map((invoice, i) => (
            <div className="pipeline-form-row" key={`inv-${i}`}>
              <label>
                PO #
                <input
                  value={invoice.poNumber}
                  onChange={(e) => setInvoices((prev) => prev.map(
                    (v, idx) => (idx === i ? { ...v, poNumber: e.target.value } : v)))}
                />
              </label>
              <label>
                Vendor
                <input
                  value={invoice.vendor} placeholder="e.g. Northgate Vac Services"
                  onChange={(e) => setInvoices((prev) => prev.map(
                    (v, idx) => (idx === i ? { ...v, vendor: e.target.value } : v)))}
                />
              </label>
              <label>
                Description
                <input
                  value={invoice.description}
                  onChange={(e) => setInvoices((prev) => prev.map(
                    (v, idx) => (idx === i ? { ...v, description: e.target.value } : v)))}
                />
              </label>
              <label>
                Value
                <input
                  type="number" min="0" value={invoice.value}
                  onChange={(e) => setInvoices((prev) => prev.map(
                    (v, idx) => (idx === i ? { ...v, value: e.target.value } : v)))}
                />
              </label>
            </div>
          ))}
          <div className="pipeline-form-row">
            <label>
              Contractor rep
              <input
                value={form.contractor_rep} placeholder="Signature (typed name)"
                onChange={(e) => setForm({ ...form, contractor_rep: e.target.value })}
              />
            </label>
            <label>
              Customer rep
              <input
                value={form.customer_rep} placeholder="Signature (typed name)"
                onChange={(e) => setForm({ ...form, customer_rep: e.target.value })}
              />
            </label>
            <label>
              Notes
              {/* Present since P4a on the wire but never in the form — surfaced by the A3
                  review: a WHOLESALE edit that cannot carry notes nulls a recorded one. */}
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </label>
          </div>
          <div className="pipeline-form-actions">
            <button
              type="button" className="pipeline-secondary-btn"
              onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
            >
              Add line
            </button>
            {/* At 41 people a superintendent picks a crew rather than typing a count (A1).
                Hours are deliberately left blank on every fanned row — there is no source for a
                standard shift until a work agreement records one. */}
            <button
              type="button" className="pipeline-secondary-btn"
              onClick={fanOutRoster}
              disabled={!rosterForPhase.length}
              title={rosterForPhase.length
                ? `Add one line per assigned person (${rosterForPhase.length})`
                : 'Everyone assigned to this phase is already on the day'}
            >
              Add crew ({rosterForPhase.length})
            </button>
            <button
              type="button" className="pipeline-secondary-btn"
              onClick={() => setInvoices((prev) => [
                ...prev, { poNumber: '', vendor: '', description: '', value: '' }])}
            >
              Add third-party invoice
            </button>
            {editingId && (
              <button
                type="button" className="pipeline-secondary-btn"
                onClick={cancelEdit}
              >
                Cancel edit
              </button>
            )}
            <button
              type="submit" className="pipeline-action-btn"
              // The demo's disabled submit: a missed day cannot record until its reason (and
              // the `other` note) is picked — the belt over the select's own `required`.
              disabled={create.isPending || missReasonIncomplete}
            >
              {create.isPending ? 'Saving…' : editingId ? 'Save day' : 'Record day'}
            </button>
          </div>
          {error && <p className="pipeline-form-error">{error}</p>}
        </form>
      </section>
      )}
    </div>
  );
};

export default FieldTimesheetTab;
